// import countries from './data/countries';
// import setCookie, { defaultExpiration } from './utils/setCookie';
import getCookie from './utils/getCookie';

/**
 * A custom element for enhancing a static BBB my account link to a dialog window
 * that shows users their currently logged in information
 *
 * @module iabbb-my-account
 */

class MyAccount extends HTMLElement {
  constructor() {
    super();

    this.render = async () => {
      await this.initData();
      if (this.user === null) {
        this.cookieInterval = window.setInterval(this.checkForLogin, 5000);
      }

      this.replaceLinkWithButton();
      this.updateButtonText();
    };
  }

  async connectedCallback() {
    await this.render();
  }

  checkForLogin = async () => {
    const isUserLoggedInCookie = getCookie('iabbb_auth_login_hint');
    if (isUserLoggedInCookie === 'true') {
      // Immediately stop checking the cookie to avoid multiple calls to initData
      clearInterval(this.cookieInterval);
      await this.initData();
      if (this.user) {
        this.updateButtonText();
      }
    }
  };

  initData = async () => {
    await fetch(`${window.headerFooterData.baseUrl}/account/api/user/current`, { credentials: 'include' }).then(
      async (res) => {
        if (res.status !== 200) {
          this.user = null;
          return;
        }

        const json = await res.json();

        if (!json?.success || !json?.email) {
          this.user = null;
          return;
        }

        this.user = {
          email: json.email,
          firstName: json.firstName,
          lastName: json.lastName,
          userType: json.userType,
          zip: json.zip,
        };
      },
    );
  };

  buildAndOpenLegacyDialog = () => {
    const closeIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M7.5 105c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l151 151 151-151c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-151 151 151 151c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-151-151-151 151c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l151-151-151-151z"/></svg>`;
    const visuallyHiddenLabel = `<span class="visually-hidden">close account dialog</span>`;

    const closeButton = document.createElement('button');
    closeButton.className = 'account-close-button bds-button-unstyled';
    closeButton.innerHTML = `${closeIcon}${visuallyHiddenLabel}`;

    const dialog = document.createElement('dialog');
    dialog.className = 'account-dialog';

    closeButton.onclick = () => {
      this.destroyLegacyDialog();
      this.querySelector('button').focus();
    };

    dialog.appendChild(closeButton);

    const dialogAccountInfo = document.createElement('div');
    dialogAccountInfo.className = 'account-info';
    dialog.appendChild(dialogAccountInfo);

    const heading = document.createElement('h2');
    heading.className = 'visually-hidden';
    heading.innerText = 'Login Information';
    dialogAccountInfo.appendChild(heading);

    if (this.user) {
      const userInfo = document.createElement('div');
      userInfo.className = 'user-info';

      const avatar = document.createElement('span');
      avatar.ariaHidden = true;
      avatar.className = 'account-avatar';
      avatar.innerText = `${this.user.firstName[0]}${this.user.lastName[0]}`;

      const userFullName = document.createElement('p');
      userFullName.innerText = `${this.user.firstName} ${this.user.lastName}`;
      userFullName.className = 'user-name';

      const userEmail = document.createElement('p');
      userEmail.innerText = this.user.email;
      userEmail.className = 'user-email';

      userInfo.appendChild(avatar);
      userInfo.appendChild(userFullName);
      userInfo.appendChild(userEmail);

      dialogAccountInfo.appendChild(userInfo);
    }
    const myAccountsHeading = document.createElement('h3');
    myAccountsHeading.innerText = 'My Accounts';
    myAccountsHeading.className = 'my-accounts-heading';

    const chevronRightIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M311.1 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L243.2 256 73.9 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/></svg>`;
    const userIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em" fill="currentColor">
            <path d="M224 248a120 120 0 1 0 0-240 120 120 0 1 0 0 240zm-29.7 56C95.8 304 16 383.8 16 482.3 16 498.7 29.3 512 45.7 512l356.6 0c16.4 0 29.7-13.3 29.7-29.7 0-98.5-79.8-178.3-178.3-178.3l-59.4 0z"/>
          </svg>`;

    const personalAccountLink = document.createElement('a');
    personalAccountLink.className = 'account-link';
    personalAccountLink.href = `${window.headerFooterData.baseUrl}/account/login`;
    personalAccountLink.innerHTML = `<span class="account-label">${userIcon}Personal Account</span>`;
    personalAccountLink.innerHTML += chevronRightIcon;

    dialogAccountInfo.appendChild(myAccountsHeading);
    dialogAccountInfo.appendChild(personalAccountLink);

    if (window.headerFooterData?.bbb?.businessLoginUrl) {
      const universityIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" width="1em" height="1em"><path d="M271.9 20.2c-9.8-5.6-21.9-5.6-31.8 0l-224 128c-12.6 7.2-18.8 22-15.1 36S17.5 208 32 208l32 0 0 208 0 0-51.2 38.4C4.7 460.4 0 469.9 0 480 0 497.7 14.3 512 32 512l448 0c17.7 0 32-14.3 32-32 0-10.1-4.7-19.6-12.8-25.6l-51.2-38.4 0-208 32 0c14.5 0 27.2-9.8 30.9-23.8s-2.5-28.8-15.1-36l-224-128zM400 208l0 208-64 0 0-208 64 0zm-112 0l0 208-64 0 0-208 64 0zm-112 0l0 208-64 0 0-208 64 0zM256 96a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>`;

      const businessAccountLink = document.createElement('a');
      businessAccountLink.className = 'account-link';
      businessAccountLink.href = window.headerFooterData?.bbb?.businessLoginUrl;
      businessAccountLink.innerHTML = `<span class="account-label">${universityIcon}Business Account</span>`;
      businessAccountLink.innerHTML += chevronRightIcon;

      dialogAccountInfo.appendChild(businessAccountLink);
    }
    if (window.headerFooterData?.bbb?.name) {
      const localBbbSection = document.createElement('div');
      localBbbSection.className = 'local-bbb-section';

      const localBbbHeading = document.createElement('h3');
      localBbbHeading.id = 'localBbbHeading';
      localBbbHeading.innerText = 'My Local BBB';
      localBbbSection.appendChild(localBbbHeading);

      const houseBuildingIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" aria-hidden="true" focusable="false" width="1em" height="1em" fill="currentColor"><path d="M368 0c-26.5 0-48 21.5-48 48V153.9l64 58.7V208c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H413.2c1.9 6.8 2.8 13.9 2.8 21.1V288h16c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H416V464c0 18-6 34.6-16 48H592c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48H368zM512 208c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H528c-8.8 0-16-7.2-16-16V208zm16 80h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H528c-8.8 0-16-7.2-16-16V304c0-8.8 7.2-16 16-16zM400 96h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H400c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16zm112 16c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H528c-8.8 0-16-7.2-16-16V112zM0 277.1V464c0 26.5 21.5 48 48 48H336c26.5 0 48-21.5 48-48V277.1c0-13.5-5.6-26.3-15.6-35.4l-144-132c-18.4-16.8-46.5-16.8-64.9 0l-144 132C5.6 250.8 0 263.7 0 277.1zM144 296c0-13.3 10.7-24 24-24h48c13.3 0 24 10.7 24 24v48c0 13.3-10.7 24-24 24H168c-13.3 0-24-10.7-24-24V296z"/></svg>`;

      const localBbbCard = document.createElement('div');
      localBbbCard.className = 'local-bbb-card';

      const localBbbLink = document.createElement('a');
      localBbbLink.className = 'local-bbb-link';
      localBbbLink.href = window.headerFooterData?.bbb?.url || '/bbb-directory';
      localBbbLink.innerHTML = `
        <span class="local-bbb-label">
          <span class="local-bbb-icon" aria-hidden="true">${houseBuildingIcon}</span>
          <span class="local-bbb-text">
            <span class="local-bbb-name">${window.headerFooterData.bbb.name}</span>
          </span>
        </span>
        <span class="local-bbb-chevron" aria-hidden="true">${chevronRightIcon}</span>
      `;

      const changeLink = document.createElement('a');
      changeLink.href = `${window.headerFooterData.baseUrl}/manage-location`;
      changeLink.id = 'changeBbbLink';
      changeLink.className = 'change-bbb-link';
      changeLink.setAttribute('aria-labelledby', 'changeBbbLink localBbbHeading');
      changeLink.innerText = 'Change';

      localBbbCard.appendChild(localBbbLink);
      localBbbCard.appendChild(changeLink);

      localBbbSection.appendChild(localBbbCard);
      dialog.appendChild(localBbbSection);
    }

    if (this.user) {
      const signOutIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em">
          <path d="M7 273c-9.4-9.4-9.4-24.6 0-33.9L135 111c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87L328 232c13.3 0 24 10.7 24 24s-10.7 24-24 24L81.9 280l87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L7 273zM344 80c-13.3 0-24-10.7-24-24s10.7-24 24-24l80 0c48.6 0 88 39.4 88 88l0 272c0 48.6-39.4 88-88 88l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l80 0c22.1 0 40-17.9 40-40l0-272c0-22.1-17.9-40-40-40l-80 0z"/>
        </svg>`;

      const signOutButton = document.createElement('button');
      signOutButton.className = 'sign-out-button bds-button-unstyled';
      signOutButton.innerHTML = `${signOutIcon} Sign Out`;
      signOutButton.onclick = async () => {
        await fetch(`${window.headerFooterData.baseUrl}/account/api/auth/sign-out`, {
          method: 'GET',
          credentials: 'include',
        }).then(() => {
          if (window.location.pathname.startsWith('/account')) {
            // WEB-13340 - account info still displays on page if user logs out from header
            window.location.href = `${window.headerFooterData.baseUrl}/account/login`;
          } else {
            this.user = null;
            this.updateButtonText();
            this.destroyLegacyDialog();
            this.querySelector('button').focus();
          }
        });
      };

      dialog.appendChild(signOutButton);
    }

    document.body.appendChild(dialog);
    this.updateDialogPosition(dialog);
    dialog.showModal();

    // Close on backdrop click
    dialog.addEventListener('click', (e) => {
      const dialogDimensions = dialog.getBoundingClientRect();
      if (
        e.clientX < dialogDimensions.left ||
        e.clientX > dialogDimensions.right ||
        e.clientY < dialogDimensions.top ||
        e.clientY > dialogDimensions.bottom
      ) {
        dialog.close();
        this.destroyLegacyDialog();
      }
    });

    // Close on Escape key (handled automatically by dialog element)
    dialog.addEventListener('close', () => {
      if (document.querySelector('.account-dialog')) {
        this.destroyLegacyDialog();
      }
      this.querySelector('button').focus();
    });

    window.addEventListener('resize', this.handleLegacyDialogPositionOnResize, { passive: true });
  };

  handleLegacyDialogPositionOnResize = () => {
    const dialogToListen = document.querySelector('.account-dialog');
    this.updateDialogPosition(dialogToListen);
  };

  updateDialogPosition = (dialog) => {
    const buttonBoundingClientRect = this.querySelector('button').getBoundingClientRect();

    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (vw < 768) {
      // 48em, Cutoff for mobile design. ignore the default value for left (40px)
      dialog.style.left = 'revert';
    } else {
      const desktopDialogWidth = 360;
      dialog.style.left = `${buttonBoundingClientRect.right - desktopDialogWidth}px`;
    }
    dialog.style.top = `${buttonBoundingClientRect.top}px`;
  };

  destroyLegacyDialog = () => {
    const dialog = document.querySelector('.account-dialog');
    if (dialog) {
      dialog.remove();
    }

    window.removeEventListener('resize', this.handleLegacyDialogPositionOnResize);
  };

  /**
   * Emit a custom event
   * @param {string} name - The event name
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (name, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  replaceLinkWithButton = () => {
    let link = this.querySelector('a');

    // link is not set when we delay its loading
    if (!link) {
      const accountIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em" fill="currentColor"><path d="M224 248a120 120 0 1 0 0-240 120 120 0 1 0 0 240zm-29.7 56C95.8 304 16 383.8 16 482.3 16 498.7 29.3 512 45.7 512l356.6 0c16.4 0 29.7-13.3 29.7-29.7 0-98.5-79.8-178.3-178.3-178.3l-59.4 0z"/></svg>`;
      link = document.createElement('a');
      link.innerHTML = `${accountIcon} My BBB`;
      link.className = 'my-account';
      link.href = `${window.headerFooterData.baseUrl}/account/login`; // this won't be used, but if something goes wrong, it's good to have as a fallback
      this.appendChild(link);
    }

    const btn = document.createElement('button');
    btn.innerHTML = link.innerHTML;

    btn.className = `${link.className} bds-button-unstyled`;
    btn.setAttribute('aria-expanded', 'false');
    btn.onclick = () => {
      this.buildAndOpenLegacyDialog();
    };

    link.replaceWith(btn);

    // Remove the inert code since dialog element handles focus trapping
  };

  updateButtonText = () => {
    const button = this.querySelector('button');

    if (this.user) {
      const userIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em" fill="currentColor"><path d="M224 248a120 120 0 1 0 0-240 120 120 0 1 0 0 240zm-29.7 56C95.8 304 16 383.8 16 482.3 16 498.7 29.3 512 45.7 512l356.6 0c16.4 0 29.7-13.3 29.7-29.7 0-98.5-79.8-178.3-178.3-178.3l-59.4 0z"/></svg>';

      button.innerHTML = `${userIcon} My BBB`;
    } else {
      const userOutlineIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" fill="currentColor" width="1em" height="1em"><path d="M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0zM48 480c0-70.7 57.3-128 128-128l96 0c70.7 0 128 57.3 128 128l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8c0-97.2-78.8-176-176-176l-96 0C78.8 304 0 382.8 0 480l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8z"/></svg>';

      button.innerHTML = `${userOutlineIcon} Sign In`;
    }
  };
}

customElements.define('iabbb-my-account', MyAccount);
